import React, { useEffect, useMemo, useState } from "react";
import { api, formatEUR } from "@/lib/api";
import { Dog, Scissors, ShoppingCart, TrendingUp, Users, Calendar, Award, Loader2 } from "lucide-react";
import { useStore } from "@/lib/storeContext";

const PET_TYPES = [
  { id: "", label: "Tutti" },
  { id: "cane", label: "Cane" },
  { id: "gatto", label: "Gatto" },
  { id: "coniglio", label: "Coniglio" },
  { id: "altro", label: "Altro" },
];

const todayISO = () => new Date().toISOString().slice(0, 10);
const addDaysISO = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

export default function ReportToelettatura() {
  const { storeId } = useStore();
  const [from, setFrom] = useState(addDaysISO(-30));
  const [to, setTo] = useState(todayISO());
  const [petType, setPetType] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ from_date: `${from}T00:00:00`, to_date: `${to}T23:59:59` });
      if (petType) params.set("pet_type", petType);
      if (storeId) params.set("store_id", storeId);
      const r = await api.get(`/reports/toelettatura?${params.toString()}`);
      setData(r.data);
    } catch { setData(null); } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [from, to, petType, storeId]);

  const maxDaily = useMemo(() => {
    if (!data?.daily?.length) return 1;
    return Math.max(...(Array.isArray(data.daily) ? data.daily : []).map(d => d.total), 1);
  }, [data]);

  return (
    <div className="p-6 lg:p-8">
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.25em] text-velta-mute mb-2">Analisi</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight flex items-center gap-3">
            <Dog className="w-8 h-8 text-velta-ink" /> Report Fatturato Toelettatura
          </h1>
          <p className="text-velta-mute mt-1">Servizi vs prodotti negozio, top clienti pet, andamento giornaliero.</p>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-velta-mute mb-1">Da</label>
            <input data-testid="rep-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-10 px-3 border border-velta-border rounded-md text-sm" />
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-velta-mute mb-1">A</label>
            <input data-testid="rep-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-10 px-3 border border-velta-border rounded-md text-sm" />
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-velta-mute mb-1">Tipo animale</label>
            <select data-testid="rep-pettype" value={petType} onChange={(e) => setPetType(e.target.value)} className="h-10 px-3 border border-velta-border rounded-md text-sm bg-white">
              {(Array.isArray(PET_TYPES) ? PET_TYPES : []).map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </div>
        </div>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-velta-mute"><Loader2 className="w-4 h-4 animate-spin" /> Caricamento…</div>
      )}

      {!loading && data && (
        <>
          {/* KPI Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <KpiCard icon={Scissors} color="#7C3AED" label="Ricavo Servizi" value={formatEUR(data?.kpi?.services_revenue)} sub={`${data?.kpi?.ratio_services_pct ?? 0}% del totale`} testid="kpi-services" />
            <KpiCard icon={ShoppingCart} color="#3B82F6" label="Ricavo Negozio" value={formatEUR(data?.kpi?.shop_revenue)} sub={`${(100 - Number(data?.kpi?.ratio_services_pct || 0)).toFixed(1)}% del totale`} testid="kpi-shop" />
            <KpiCard icon={TrendingUp} color="#2F9E77" label="Fatturato Totale" value={formatEUR(data?.kpi?.total_revenue)} sub={`${data?.kpi?.total_receipts ?? 0} scontrini · media ${formatEUR(data?.kpi?.avg_ticket)}`} testid="kpi-total" />
            <KpiCard icon={Users} color="#F59E0B" label="Animali Serviti" value={data?.kpi?.unique_pets_serviced ?? 0} sub={`${data?.kpi?.appointments_completed ?? 0} appuntamenti completati`} testid="kpi-pets" />
          </div>

          {/* Grafico giornaliero */}
          {data?.daily?.length > 0 && (
            <div className="bg-white border border-velta-border rounded-md p-6 mb-6">
              <h2 className="font-heading font-semibold mb-4 flex items-center gap-2"><Calendar className="w-4 h-4" /> Andamento giornaliero</h2>
              <div className="flex items-end gap-1 h-40 overflow-x-auto pos-scroll" data-testid="rep-daily-chart">
                {(Array.isArray(data.daily) ? data.daily : []).slice(-14).map(d => (
                  <div key={d.date} className="flex flex-col items-center shrink-0" style={{ minWidth: "36px" }}>
                    <div className="w-full flex flex-col items-stretch h-32 justify-end" title={`${d.date}: ${formatEUR(d.total)}`}>
                      <div style={{ height: `${(d.services / maxDaily) * 100}%`, background: "#7C3AED" }} className="w-full transition-all" />
                      <div style={{ height: `${(d.shop / maxDaily) * 100}%`, background: "#3B82F6" }} className="w-full transition-all" />
                    </div>
                    <div className="text-[9px] font-mono text-velta-mute mt-1 -rotate-45 origin-top-left h-4 pl-2">{d.date.slice(5)}</div>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-4 mt-4 text-[11px]">
                <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 bg-[#7C3AED] rounded-sm" /> Servizi</span>
                <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 bg-[#3B82F6] rounded-sm" /> Negozio</span>
              </div>
            </div>
          )}

          {/* Top listings */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
            <ListCard title="Top Servizi" icon={Scissors} color="#7C3AED" items={data?.top_services} format={(x) => `${x.count}× · ${formatEUR(x.revenue)}`} testid="rep-top-svc" empty="Nessun servizio venduto nel periodo" />
            <ListCard title="Top Prodotti Negozio" icon={ShoppingCart} color="#3B82F6" items={data?.top_shop_products} format={(x) => `${x.count}× · ${formatEUR(x.revenue)}`} testid="rep-top-shop" empty="Nessun prodotto negozio venduto" />
            <ListCard title="Ricavo per Tipo Animale" icon={Dog} color="#2F9E77" items={(Array.isArray(data.revenue_by_pet_type) ? data.revenue_by_pet_type : []).map(x => ({ name: x.pet_type, ...x }))} format={(x) => `${x.appointments} appt. · ${formatEUR(x.revenue)}`} testid="rep-by-type" empty="Nessun appuntamento completato" />
          </div>

          {/* Top pets */}
          <div className="bg-white border border-velta-border rounded-md p-6">
            <h2 className="font-heading font-semibold mb-4 flex items-center gap-2"><Award className="w-4 h-4 text-[#F59E0B]" /> Top Animali (per visite)</h2>
            {(!data?.top_pets || data.top_pets.length === 0) ? (
              <p className="text-sm text-velta-mute">Nessun animale con appuntamenti nel periodo selezionato.</p>
            ) : (
              <table className="w-full text-sm" data-testid="rep-top-pets-table">
                <thead>
                  <tr className="text-left text-[10px] font-mono uppercase tracking-widest text-velta-mute border-b border-velta-border">
                    <th className="pb-2 w-8">#</th>
                    <th className="pb-2">Animale</th>
                    <th className="pb-2">Razza</th>
                    <th className="pb-2">Proprietario</th>
                    <th className="pb-2 text-right">Visite</th>
                    <th className="pb-2 text-right">Spesa totale</th>
                  </tr>
                </thead>
                <tbody>
                  {(Array.isArray(data.top_pets) ? data.top_pets : []).map((p, i) => (
                    <tr key={i} className="border-b border-velta-border/40 hover:bg-velta-bg">
                      <td className="py-2 font-mono text-velta-mute">{i + 1}</td>
                      <td className="py-2 font-semibold">{p.pet_name}</td>
                      <td className="py-2 text-velta-mute">{p.pet_breed || "—"}</td>
                      <td className="py-2">{p.owner_name || "—"}</td>
                      <td className="py-2 text-right font-mono">{p.visits}</td>
                      <td className="py-2 text-right font-mono font-bold">{formatEUR(p.total_spend)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function KpiCard({ icon: Icon, color, label, value, sub, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-velta-border rounded-md p-5 relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-1" style={{ background: color }} />
      <div className="flex items-center gap-2 mb-3 text-velta-mute">
        <Icon className="w-4 h-4" style={{ color }} />
        <span className="text-[10px] font-mono uppercase tracking-widest">{label}</span>
      </div>
      <div className="font-mono font-bold text-3xl tracking-tighter mb-1">{value}</div>
      <div className="text-[11px] text-velta-mute">{sub}</div>
    </div>
  );
}

function ListCard({ title, icon: Icon, color, items, format, testid, empty }) {
  return (
    <div data-testid={testid} className="bg-white border border-velta-border rounded-md p-5">
      <h3 className="font-heading font-semibold mb-3 flex items-center gap-2">
        <Icon className="w-4 h-4" style={{ color }} /> {title}
      </h3>
      {(items?.length ?? 0) === 0 ? (
        <p className="text-xs text-velta-mute">{empty}</p>
      ) : (
        <ol className="space-y-2">
          {(Array.isArray(items) ? items : []).map((x, i) => (
            <li key={i} className="flex items-center gap-2 text-sm">
              <span className="w-5 text-xs font-mono text-velta-mute">{i + 1}.</span>
              <span className="flex-1 truncate capitalize">{x.name}</span>
              <span className="text-xs font-mono text-velta-mute">{format(x)}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
